import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TOTPInput } from '@/components/auth/TOTPInput';
import { twoFactorApi } from '@/services/two-factor';
import styles from './TwoFactorSetup.module.css';

type SetupStep = 'loading' | 'qr-code' | 'verify' | 'backup-codes' | 'success';

export function TwoFactorSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState<SetupStep>('loading');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [manualKey, setManualKey] = useState<string>('');
  const [showManualKey, setShowManualKey] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [clearTrigger, setClearTrigger] = useState(0);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Start 2FA setup on mount
  useEffect(() => {
    startSetup();
  }, []);

  const startSetup = async () => {
    setStep('loading');
    setError('');

    const response = await twoFactorApi.startSetup();

    if (response.success && response.data) {
      setQrCodeUrl(response.data.qrCodeUrl);
      setManualKey(response.data.manualEntryKey);
      setStep('qr-code');
    } else {
      setError(response.error?.message || 'Failed to start 2FA setup. Please try again.');
      setStep('qr-code');
    }
  };

  const handleVerifyCode = async (code: string) => {
    setIsVerifying(true);
    setError('');

    const response = await twoFactorApi.verifySetup(code);

    if (response.success && response.data) {
      setBackupCodes(response.data.backupCodes);
      setStep('backup-codes');
    } else {
      setError(response.error?.message || 'Invalid code. Please try again.');
      setClearTrigger(prev => prev + 1);
    }

    setIsVerifying(false);
  };

  const handleCopyBackupCodes = async () => {
    const codesText = backupCodes.join('\n');
    try {
      await navigator.clipboard.writeText(codesText);
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = codesText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
    }
  };

  const handleDownloadBackupCodes = () => {
    const codesText = `OHCS Records Management System - 2FA Backup Codes
Generated: ${new Date().toLocaleString()}

IMPORTANT: Keep these codes safe! Each code can only be used once.

${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

If you lose access to your authenticator app, you can use these codes to sign in.
`;

    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ohcs-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleComplete = () => {
    setStep('success');
    // Navigate after a brief delay to show success
    setTimeout(() => {
      navigate('/settings/security', { replace: true });
    }, 2000);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Format manual key for display (groups of 4)
  const formatManualKey = (key: string) => {
    return key.match(/.{1,4}/g)?.join(' ') || key;
  };

  return (
    <div className={styles.page}>
      {/* Background particles */}
      <div className={styles.particlesContainer}>
        {[...Array(15)].map((_, i) => (
          <div key={i} className={styles.particle} style={{
            '--delay': `${i * 0.7}s`,
            '--x': `${Math.random() * 100}%`,
            '--duration': `${18 + Math.random() * 15}s`,
          } as React.CSSProperties} />
        ))}
      </div>

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <button onClick={handleCancel} className={styles.backButton} aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className={styles.headerContent}>
            <div className={styles.shieldIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <h1 className={styles.title}>Two-Factor Authentication</h1>
            <p className={styles.subtitle}>Add an extra layer of security to your account</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className={styles.progressSteps}>
          <div className={`${styles.progressStep} ${step !== 'loading' ? styles.completed : ''} ${step === 'loading' ? styles.active : ''}`}>
            <div className={styles.stepNumber}>1</div>
            <span className={styles.stepLabel}>Scan QR</span>
          </div>
          <div className={styles.progressLine} />
          <div className={`${styles.progressStep} ${['backup-codes', 'success'].includes(step) ? styles.completed : ''} ${step === 'verify' ? styles.active : ''}`}>
            <div className={styles.stepNumber}>2</div>
            <span className={styles.stepLabel}>Verify</span>
          </div>
          <div className={styles.progressLine} />
          <div className={`${styles.progressStep} ${step === 'success' ? styles.completed : ''} ${step === 'backup-codes' ? styles.active : ''}`}>
            <div className={styles.stepNumber}>3</div>
            <span className={styles.stepLabel}>Save Codes</span>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Loading State */}
          {step === 'loading' && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Setting up two-factor authentication...</p>
            </div>
          )}

          {/* QR Code Step */}
          {step === 'qr-code' && (
            <div className={styles.stepContent}>
              <h2 className={styles.stepTitle}>Scan QR Code</h2>
              <p className={styles.stepDescription}>
                Use an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator
                to scan this QR code.
              </p>

              {error && !qrCodeUrl && (
                <div className={styles.errorBox}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4M12 16h.01" />
                  </svg>
                  <span>{error}</span>
                  <button onClick={startSetup} className={styles.retryButton}>
                    Retry
                  </button>
                </div>
              )}

              {qrCodeUrl && (
                <>
                  <div className={styles.qrCodeContainer}>
                    <img src={qrCodeUrl} alt="2FA QR Code" className={styles.qrCode} />
                    <div className={styles.qrCodeOverlay}>
                      <div className={styles.qrCorner} style={{ top: 0, left: 0 }} />
                      <div className={styles.qrCorner} style={{ top: 0, right: 0 }} />
                      <div className={styles.qrCorner} style={{ bottom: 0, left: 0 }} />
                      <div className={styles.qrCorner} style={{ bottom: 0, right: 0 }} />
                    </div>
                  </div>

                  <button
                    onClick={() => setShowManualKey(!showManualKey)}
                    className={styles.manualEntryToggle}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    {showManualKey ? 'Hide manual entry key' : 'Can\'t scan? Enter key manually'}
                  </button>

                  {showManualKey && (
                    <div className={styles.manualKeyBox}>
                      <p className={styles.manualKeyLabel}>Enter this key in your authenticator app:</p>
                      <div className={styles.manualKey}>
                        <code>{formatManualKey(manualKey)}</code>
                        <button
                          onClick={() => navigator.clipboard.writeText(manualKey)}
                          className={styles.copyButton}
                          title="Copy key"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setStep('verify')}
                    className={styles.primaryButton}
                  >
                    Continue
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          )}

          {/* Verify Step */}
          {step === 'verify' && (
            <div className={styles.stepContent}>
              <h2 className={styles.stepTitle}>Verify Code</h2>
              <p className={styles.stepDescription}>
                Enter the 6-digit code from your authenticator app to verify the setup.
              </p>

              <div className={styles.totpContainer}>
                <TOTPInput
                  onComplete={handleVerifyCode}
                  disabled={isVerifying}
                  error={error}
                  clearTrigger={clearTrigger}
                  autoFocus
                />
              </div>

              {isVerifying && (
                <div className={styles.verifyingState}>
                  <div className={styles.spinnerSmall} />
                  <span>Verifying code...</span>
                </div>
              )}

              <button
                onClick={() => setStep('qr-code')}
                className={styles.secondaryButton}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back to QR Code
              </button>
            </div>
          )}

          {/* Backup Codes Step */}
          {step === 'backup-codes' && (
            <div className={styles.stepContent}>
              <div className={styles.successIcon}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <path d="M22 4L12 14.01l-3-3" />
                </svg>
              </div>

              <h2 className={styles.stepTitle}>Save Your Backup Codes</h2>
              <p className={styles.stepDescription}>
                These codes can be used to access your account if you lose your authenticator device.
                Each code can only be used once. Keep them safe!
              </p>

              <div className={styles.backupCodesContainer}>
                <div className={styles.backupCodesGrid}>
                  {backupCodes.map((code, index) => (
                    <div key={index} className={styles.backupCode}>
                      <span className={styles.codeNumber}>{index + 1}.</span>
                      <code>{code}</code>
                    </div>
                  ))}
                </div>

                <div className={styles.backupCodesActions}>
                  <button onClick={handleCopyBackupCodes} className={styles.actionButton}>
                    {copiedCodes ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Copy Codes
                      </>
                    )}
                  </button>
                  <button onClick={handleDownloadBackupCodes} className={styles.actionButton}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download
                  </button>
                </div>
              </div>

              <div className={styles.warningBox}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div>
                  <strong>Important:</strong> Store these codes in a safe place. You won't be able to see them again!
                </div>
              </div>

              <button onClick={handleComplete} className={styles.primaryButton}>
                I've Saved My Codes
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className={styles.stepContent}>
              <div className={styles.successAnimation}>
                <div className={styles.successCircle}>
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
              </div>
              <h2 className={styles.stepTitle}>2FA Enabled!</h2>
              <p className={styles.stepDescription}>
                Your account is now protected with two-factor authentication.
                Redirecting to security settings...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TwoFactorSetup;
